import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from "@angular/core";

const CM_TO_PX = 37.795275591;
const DEFAULT_WIDTH_CM = 9;
const DEFAULT_HEIGHT_CM = 5;
const THUMB_READY_TIMEOUT_MS = 5000;
const THUMB_READY_MESSAGE = "pc-print-card-ready";

@Component({
  selector: "app-print-card-layout-thumb",
  templateUrl: "./print-card-layout-thumb.component.html",
  styleUrls: ["./print-card-layout-thumb.component.scss"],
  standalone: false,
})
export class PrintCardLayoutThumbComponent
  implements OnChanges, AfterViewInit, OnDestroy
{
  @Input() documentHtml: string | null | undefined;
  @Input() pageWidthCm = DEFAULT_WIDTH_CM;
  @Input() pageHeightCm = DEFAULT_HEIGHT_CM;
  @Input() loading = false;

  @ViewChild("frame", { static: true })
  frameRef?: ElementRef<HTMLIFrameElement>;

  settling = false;

  private resizeObserver?: ResizeObserver;
  private resizeFrameId = 0;
  private readyCleanup?: () => void;

  constructor(
    private readonly hostRef: ElementRef<HTMLElement>,
    private readonly ngZone: NgZone,
  ) {}

  get hasHtml(): boolean {
    return Boolean(this.documentHtml?.trim());
  }

  get showOverlay(): boolean {
    return this.loading || this.settling;
  }

  ngAfterViewInit(): void {
    this.bindResizeObserver();
    this.render();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes["documentHtml"] ||
      changes["pageWidthCm"] ||
      changes["pageHeightCm"]
    ) {
      this.render();
    }
  }

  ngOnDestroy(): void {
    this.clearReadyWaiter();
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    if (this.resizeFrameId) {
      cancelAnimationFrame(this.resizeFrameId);
      this.resizeFrameId = 0;
    }
  }

  onFrameLoad(): void {
    const frame = this.frameRef?.nativeElement;
    if (!frame) {
      this.finishSettling();
      return;
    }
    this.fitFrame(frame);
    this.waitForContentReady(frame);
  }

  private get sourceWidthPx(): number {
    return Math.max(1, Math.round(this.pageWidthCm * CM_TO_PX));
  }

  private get sourceHeightPx(): number {
    return Math.max(1, Math.round(this.pageHeightCm * CM_TO_PX));
  }

  private render(): void {
    const frame = this.frameRef?.nativeElement;
    if (!frame) {
      return;
    }
    this.clearReadyWaiter();
    const html = this.documentHtml?.trim() ?? "";
    this.settling = html.length > 0;
    frame.srcdoc =
      html ||
      "<!DOCTYPE html><html><body style='margin:0;background:#fff'></body></html>";
    this.fitFrame(frame);
    if (!html) {
      this.settling = false;
    }
  }

  private waitForContentReady(frame: HTMLIFrameElement): void {
    this.clearReadyWaiter();
    if (!this.settling) {
      return;
    }

    const finish = (): void => {
      this.clearReadyWaiter();
      this.finishSettling();
      this.fitFrame(frame);
    };

    try {
      const win = frame.contentWindow as
        (Window & { EuroPDF?: { readyToRender?: () => boolean } }) | null;
      if (win?.EuroPDF?.readyToRender?.()) {
        finish();
        return;
      }
      if (!win?.EuroPDF?.readyToRender) {
        finish();
        return;
      }
    } catch {
      finish();
      return;
    }

    const onMessage = (event: MessageEvent): void => {
      if (event.source !== frame.contentWindow) {
        return;
      }
      const data = event.data as { type?: string } | null;
      if (!data || data.type !== THUMB_READY_MESSAGE) {
        return;
      }
      this.ngZone.run(finish);
    };

    window.addEventListener("message", onMessage);
    const timeoutId = window.setTimeout(() => {
      this.ngZone.run(finish);
    }, THUMB_READY_TIMEOUT_MS);

    this.readyCleanup = () => {
      window.removeEventListener("message", onMessage);
      window.clearTimeout(timeoutId);
    };
  }

  private finishSettling(): void {
    this.settling = false;
  }

  private clearReadyWaiter(): void {
    if (!this.readyCleanup) {
      return;
    }
    this.readyCleanup();
    this.readyCleanup = undefined;
  }

  private bindResizeObserver(): void {
    const host = this.hostRef.nativeElement;
    if (typeof ResizeObserver === "undefined" || this.resizeObserver) {
      return;
    }
    this.resizeObserver = new ResizeObserver(() => {
      this.ngZone.runOutsideAngular(() => {
        this.scheduleFit();
      });
    });
    this.resizeObserver.observe(host);
  }

  private scheduleFit(): void {
    if (this.resizeFrameId) {
      return;
    }
    this.resizeFrameId = requestAnimationFrame(() => {
      this.resizeFrameId = 0;
      const frame = this.frameRef?.nativeElement;
      if (frame) {
        this.fitFrame(frame);
      }
    });
  }

  private fitFrame(frame: HTMLIFrameElement): void {
    const host = this.hostRef.nativeElement;
    const sourceW = this.sourceWidthPx;
    const sourceH = this.sourceHeightPx;
    host.style.aspectRatio = `${this.pageWidthCm} / ${this.pageHeightCm}`;
    host.style.height = "";
    const hostWidth = Math.max(1, host.clientWidth);
    const hostHeight = Math.max(
      1,
      host.clientHeight || Math.round((hostWidth * sourceH) / sourceW),
    );
    const scale = Math.min(hostWidth / sourceW, hostHeight / sourceH);
    frame.width = String(sourceW);
    frame.height = String(sourceH);
    frame.style.width = `${sourceW}px`;
    frame.style.height = `${sourceH}px`;
    frame.style.transform = `translate(-50%, -50%) scale(${scale})`;
  }
}
